// A GIF taken apart by hand.
//
// `ImageDecoder` (WebCodecs) does this in one call in Chrome. WebKit does not
// have it — on the phone, every GIF fax came out BLANK: the preview was an
// empty sheet and SEND said "THE PAGE DID NOT PRINT", while the very same
// build on the website was fine. A fax machine with a Safari-shaped hole in it
// is not a fax machine, so this is the decoder that is always there.
//
// It is the mirror of gif.js: GIF87a/89a, global and local palettes, LZW with
// the KwKwK case, interlace, and the three disposal methods composited onto a
// full-size RGBA canvas so a frame that only paints the pixels that changed
// still comes out whole. Transparent pixels stay transparent; the fax render
// draws them on paper.

/** Parse the byte stream into raw indexed frames. No pixels are composited yet. */
export function parseGif(bytes) {
  const b = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let p = 0;
  const u16 = () => { const v = b[p] | (b[p + 1] << 8); p += 2; return v; };
  const skipSubBlocks = () => { for (;;) { const n = b[p++]; if (!n || p > b.length) break; p += n; } };

  if (String.fromCharCode(b[0], b[1], b[2]) !== 'GIF') {
    throw Object.assign(new Error('That is not a GIF.'), { code: 'not-gif' });
  }
  p = 6;
  const width = u16();
  const height = u16();
  const packed = b[p++];
  p += 2; // background index, aspect ratio: neither matters here
  let globalPalette = null;
  if (packed & 0x80) {
    const n = 2 << (packed & 7);
    globalPalette = b.subarray(p, p + n * 3);
    p += n * 3;
  }

  const frames = [];
  let gce = null;
  while (p < b.length) {
    const block = b[p++];
    if (block === 0x3B) break;                        // trailer
    if (block === 0x21) {                             // extension
      const label = b[p++];
      if (label === 0xF9) {                           // graphic control
        const size = b[p++];
        const flags = b[p];
        const delay = b[p + 1] | (b[p + 2] << 8);
        const ti = b[p + 3];
        p += size;
        gce = { disposal: (flags >> 2) & 7, transparent: (flags & 1) ? ti : -1, delayMs: delay * 10 };
      }
      skipSubBlocks();
      continue;
    }
    if (block !== 0x2C) break;                        // image descriptor or nothing we know
    const left = u16();
    const top = u16();
    const w = u16();
    const h = u16();
    const ip = b[p++];
    let palette = globalPalette;
    if (ip & 0x80) {
      const n = 2 << (ip & 7);
      palette = b.subarray(p, p + n * 3);
      p += n * 3;
    }
    const interlaced = !!(ip & 0x40);
    const minCodeSize = b[p++];
    const chunks = [];
    let total = 0;
    for (;;) {
      const n = b[p++];
      if (!n || p > b.length) break;
      chunks.push(b.subarray(p, p + n));
      total += n;
      p += n;
    }
    const data = new Uint8Array(total);
    let o = 0;
    for (const c of chunks) { data.set(c, o); o += c.length; }
    frames.push({
      left, top, w, h, palette, interlaced, data, minCodeSize,
      disposal: gce?.disposal ?? 0,
      transparent: gce?.transparent ?? -1,
      delayMs: gce?.delayMs ?? 0,
    });
    gce = null;
  }
  if (!frames.length) throw Object.assign(new Error('That GIF has no frames.'), { code: 'empty' });
  return { width, height, frames };
}

/** LZW, GIF flavour: variable code size 3..12 bits, LSB-first, CLEAR and EOI. */
export function lzwDecode(data, minCodeSize, pixelCount) {
  const out = new Uint8Array(pixelCount);
  const clear = 1 << minCodeSize;
  const eoi = clear + 1;
  const prefix = new Int32Array(4096);
  const suffix = new Uint8Array(4096);
  const first = new Uint8Array(4096);
  const length = new Uint16Array(4096);
  for (let i = 0; i < clear; i++) { prefix[i] = -1; suffix[i] = i; first[i] = i; length[i] = 1; }

  let codeSize = minCodeSize + 1;
  let codeMask = (1 << codeSize) - 1;
  let dictSize = eoi + 1;
  let prev = -1;
  let bitBuf = 0;
  let bitCnt = 0;
  let pos = 0;
  let op = 0;

  while (op < pixelCount) {
    while (bitCnt < codeSize) {
      if (pos >= data.length) return out;              // ran out: what we have
      bitBuf |= data[pos++] << bitCnt;
      bitCnt += 8;
    }
    const code = bitBuf & codeMask;
    bitBuf >>>= codeSize;
    bitCnt -= codeSize;

    if (code === clear) {
      codeSize = minCodeSize + 1;
      codeMask = (1 << codeSize) - 1;
      dictSize = eoi + 1;
      prev = -1;
      continue;
    }
    if (code === eoi) break;
    if (code > dictSize || (code === dictSize && prev === -1)) return out; // corrupt

    // The string for this code starts with... itself, or (KwKwK) the previous
    // string's first character. Either way the new dictionary entry is
    // previous + that character.
    const f = code < dictSize ? first[code] : first[prev];
    if (prev !== -1 && dictSize < 4096) {
      prefix[dictSize] = prev;
      suffix[dictSize] = f;
      first[dictSize] = first[prev];
      length[dictSize] = length[prev] + 1;
      dictSize++;
      if (dictSize === (1 << codeSize) && codeSize < 12) {
        codeSize++;
        codeMask = (1 << codeSize) - 1;
      }
    }

    // Emit, walking back to the root and filling right-to-left.
    const len = length[code];
    let c = code;
    let i = Math.min(len, pixelCount - op);
    const base = op;
    op += i;
    // Characters beyond the output buffer are dropped from the far end first.
    for (let skip = len - i; skip > 0; skip--) c = prefix[c];
    while (i > 0) { out[base + --i] = suffix[c]; c = prefix[c]; }
    prev = code;
  }
  return out;
}

const INTERLACE = [[0, 8], [4, 8], [2, 4], [1, 2]];

/**
 * Composite the parsed frames and hand back ImageBitmaps for the ones asked
 * for, with each frame's own delay. Every frame is decoded in order — a GIF
 * frame is a patch over the last one — but bitmaps are only made for `want`.
 */
export async function compositeGif(parsed, want) {
  const { width, height, frames } = parsed;
  const wanted = new Set(want);
  const canvas = new Uint8ClampedArray(width * height * 4);
  const bitmaps = [];
  const delaysMs = [];
  let saved = null;

  for (let fi = 0; fi < frames.length; fi++) {
    const f = frames[fi];
    if (f.disposal === 3) saved = canvas.slice();
    const idx = lzwDecode(f.data, f.minCodeSize, f.w * f.h);
    const pal = f.palette;
    // Row order: straight, or the four interlace passes.
    let rowOf = (r) => r;
    if (f.interlaced) {
      const map = new Uint32Array(f.h);
      let r = 0;
      for (const [start, step] of INTERLACE) for (let y = start; y < f.h; y += step) map[r++] = y;
      rowOf = (r2) => map[r2];
    }
    if (pal) {
      for (let r = 0; r < f.h; r++) {
        const y = f.top + rowOf(r);
        if (y >= height) continue;
        const rowBase = r * f.w;
        for (let x = 0; x < f.w; x++) {
          const ci = idx[rowBase + x];
          if (ci === f.transparent) continue;
          const px = f.left + x;
          if (px >= width) continue;
          const o = (y * width + px) * 4;
          const pi = ci * 3;
          canvas[o] = pal[pi];
          canvas[o + 1] = pal[pi + 1];
          canvas[o + 2] = pal[pi + 2];
          canvas[o + 3] = 255;
        }
      }
    }
    if (wanted.has(fi)) {
      bitmaps.push(await createImageBitmap(new ImageData(canvas.slice(), width, height)));
      delaysMs.push(f.delayMs);
    }
    if (f.disposal === 2) {
      for (let r = 0; r < f.h; r++) {
        const y = f.top + r;
        if (y >= height) continue;
        const o = (y * width + f.left) * 4;
        canvas.fill(0, o, o + Math.min(f.w, width - f.left) * 4);
      }
    } else if (f.disposal === 3 && saved) {
      canvas.set(saved);
      saved = null;
    }
  }
  return { bitmaps, delaysMs };
}
